import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone } from "@/lib/DataUtils";
import { createStore } from "../storeHelper";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:UsersStore`);

const DEFAULT_EDITOR = {
  userId: null,
  email: null,
  password: null,
  organisationId: null,
  roles: null,
};

export const useUsersStore = defineStore(
  "users",
  createStore({
    state: {
      users: {},
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      usersList: (state) => _.values(state.users),
      userById: (state) => (userId) => state.users[userId],
      usersInOrganisation: (state) => (organisationId) => {
        return _.values(state.users).filter(
          (x) => x.organisationId === organisationId,
        );
      },
      editorChanges() {
        return this.getEditorChanges("users");
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      newUserToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      addUser(user) {
        this.users[user.userId] = user;
      },

      deleteUser(userId) {
        delete this.users[userId];
      },

      async load() {
        debug("load");
        const users = await ApiClient.get(`/users`);
        debug("load", users);
        this.set("users", _.keyBy(users, "userId"));
      },

      async loadUserToEditor(userId) {
        debug("loadUserToEditor", userId);
        const user = this.users[userId];
        if (!user) {
          throw new Error("User not found");
        }
        this.set("editor", sureClone(user));
      },

      async createUser() {
        const user = sureClone(this.editor);
        _.unset(user, "userId");
        debug("createUser", user);
        const response = await ApiClient.post("/users", user);
        debug("createUser - response", response);
        this.addUser(response);
        return response;
      },

      async saveUserInEditor() {
        const userId = this.editor.userId;
        if (!this.editorHasChanges) {
          debug("saveUserInEditor", "No changes to save");
          return;
        }
        const modifiedUser = sureClone(this.editor);
        debug("saveUserInEditor", userId, modifiedUser);
        const result = await ApiClient.put(`/users/${userId}`, modifiedUser);
        this.addUser(result);
        debug("saveUserInEditor, result", result);
        return result;
      },

      async deleteUserById(userId) {
        debug("deleteUser", userId);
        await ApiClient.delete(`/users/${userId}`);
        this.deleteUser(userId);
      },
    },
  }),
);
